import { inject, Injectable } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiFragment, Family } from '@stylehaven/components';
import { saveAs } from 'file-saver';
import { Mime } from 'mime';

import streamSaver from 'streamsaver';

import { LocationStrategy } from '@angular/common';
import otherTypes from 'mime/types/other.js';
import standardTypes from 'mime/types/standard.js';
import { firstValueFrom, from, Observable, take } from 'rxjs';
import { TokenInterceptor } from '../../../authentication/interceptors/token.interceptor';
import { SearchService } from '../api/search.service';
import { ReplaceUnsupportedCharacters } from '../exports/replace-unsupported-characters.helper';
import { isSecureDownload } from './is-secure-download.helper';

@Injectable({ providedIn: 'root' })
export class DownloadsService {
    private mime: Mime;

    // Injectors
    private readonly http: HttpClient = inject(HttpClient);
    private readonly recordService: SearchService = inject(SearchService);
    private readonly tokenInterceptor: any = inject(TokenInterceptor);
    private readonly locationStrategy: LocationStrategy = inject(LocationStrategy);

    constructor() {
        this.mime = new Mime(standardTypes, otherTypes); // Modifying default mime is not allowed, so we mimic it
        this.mime.define({ 'image/jpeg': ['jpg'] }, true); // Override jpg mapping to use jpg instead of jpeg

        const baseHref = this.locationStrategy.getBaseHref();

        streamSaver.mitm = `${baseHref}assets/streamsaver/mitm.html`;
    }

    public async downloadFragmentPreview(record: ApiFragment): Promise<void> {
        const url: string = record.getAccessPreviewUrl();

        const blob: Blob = await firstValueFrom(this.http.get<Blob>(url, { responseType: 'blob' as 'json' }));
        const downloadName: string = this.getDownloadTitle(record, blob.type);

        saveAs(blob, downloadName);
        this.registerDownloadEvent(record);
    }

    private registerDownloadEvent(record: ApiFragment): void {
        this.recordService.registerDownload(record.getFragmentId());
    }

    private getDownloadTitle(record: ApiFragment, mimeType: string): string {
        const originalFilename: string = record.get(Family.Descriptive, 'OriginalFilename');
        const cleanFilename: string = ReplaceUnsupportedCharacters(originalFilename);
        const filenameWithoutExtension: string = cleanFilename.replace(/\.[^/.]+$/, '');
        const extension: string | null = this.mime.getExtension(mimeType);

        return `${filenameWithoutExtension}.${extension}`;
    }

    public downloadUrl(url: string, filename: string): void {
        if (isSecureDownload(url)) {
            this.authenticatedDownload(url, filename).pipe(take(1)).subscribe();
        } else {
            this.linkDownload(url, filename);
        }
    }

    private authenticatedDownload(url: string, filename: string): Observable<void> {
        const httpHeaders: HttpHeaders = this.tokenInterceptor.getAuthorizationHeader();
        const headers = {
            Authorization: httpHeaders.get('Authorization') || ''
        };

        const response = fetch(url, {
            headers
        });

        const filestream = response.then(response => {
            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream not available');
            }

            const fileStream = streamSaver.createWriteStream(filename);

            return response.body.pipeTo(fileStream);
        });

        return from(filestream);
    }

    private linkDownload(url: string, filename: string): void {
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
    }
}
